import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setIsPaymentError, setPaymentStage, setPaymentURL } from "../actions/payment";
import { HTTPMethods } from "../yoello-lib/modules/API/API";
import APIExtensions from "../yoello-lib/modules/API/APIDefs";
import useAPI from "../yoello-lib/modules/API/useAPI";
// import { useAuth0 } from "../yoello-lib/modules/auth/Auth0";
import { usePaymentState } from "./usePaymentState"
import useTask from "./useTask";
import { PaymentProcessor } from "../components/menu/payments/types.d"
import { IReduxAppState } from "../reducers";
import { PERMISSIONS } from "../const/permissions.const";
import { useAuthenticationContext } from "../yoello-lib/modules/auth/AuthenticationContext";
interface StripeAccount {
    details_submitted?: boolean;
    disabled_reason?: string | null;
    payouts_enabled?: boolean;
    stripe_connect_account_id?: string;
    currently_due: string[] | null;
    eventually_due: string[] | null;
    past_due: string[] | null;
}

interface StripeOnboard {
    stripe_connect_account_id?: string;
    expires_at?: number;
    onboarding_url?: string;
}

interface StripeLogin {
    stripe_connect_account_id?: string;
    expires_at?: number;
    login_url?: string;
}

export function useStripeAccount(venueId: string) {
    const { t } = useTranslation();
    const { increaseDoneTasks } = useTask();
    const { retrieveAccessToken } = useAuthenticationContext();
    const { paymentDetails } = usePaymentState();
    const [stripeAccountDataLoaded, setStripeAccountDataLoaded] = useState<boolean>(false);
    const [stripeStage, setStripeStage] = useState<number>(-1);
    const [stripeURL, setStripeURL] = useState<string | null>(null);
    const permissions = useSelector((state: IReduxAppState) => state.permissions);
    const dispatch = useDispatch();

    const { callAPI: stripeAccount, isError: stripeAccountIsError } = useAPI<StripeAccount>(APIExtensions.stripeAccount, retrieveAccessToken, {
        preQuery: venueId,
        onMountDisabled: true,
        errorCall: t('payments.api.stripeOnboard.error'),
    });
    const { callAPI: stripeOnboard, isError: stripeOnboardIsError } = useAPI<StripeOnboard>(APIExtensions.stripeOnboarding, retrieveAccessToken, {
        preQuery: venueId,
        onMountDisabled: true,
        errorCall: t('payments.api.stripeOnboard.error'),
    });
    const { callAPI: stripeLogin, isError: stripeLoginIsError } = useAPI<StripeLogin>(APIExtensions.stripeLogin, retrieveAccessToken, {
        preQuery: venueId,
        onMountDisabled: true,
        errorCall: t('payments.api.stripeLogin.error'),
    });


    useEffect(() => {
        if (!venueId || !permissions || !permissions[PERMISSIONS.BILLING_AND_PAYMENT]) return;

        setStripeAccountDataLoaded(false);

        const fetchData = async () => {
            await stripeAccount(HTTPMethods.PUT, {
                errorCall: t('menu.payment.stripeAccountFail'),
            });
            // Set to empty string if no stripe_connect_account_id. Assuming it's a 204 because account is already created but cannot check response status code
            // setStripeConnectedAccountId(newAccount?.stripe_connect_account_id || "")
            setStripeAccountDataLoaded(true);
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId, permissions]);

    useEffect(() => {
        if (!venueId || !permissions || !permissions[PERMISSIONS.BILLING_AND_PAYMENT]) return;
        if (!stripeAccountDataLoaded) {
            return;
        }
        const fetchData = async () => {
            // if (!stripeConnectedAccountId  || stripeConnectedAccountId.length === 0) return;  // Prevent useEffect onMount
            if (!stripeAccountDataLoaded) { return; }

            const accountResponse = await stripeAccount(HTTPMethods.GET, {
                errorCall: t('menu.payment.stripeOnboardFail'),
            });

            if (accountResponse?.details_submitted === false && paymentDetails.paymentProcessor !== PaymentProcessor.PAY360) {
                setStripeStage(0);
            }

            if (accountResponse?.details_submitted === true &&
                accountResponse?.payouts_enabled === false &&
                ((accountResponse?.currently_due && accountResponse?.currently_due.length > 0) ||
                    (accountResponse?.eventually_due && accountResponse?.eventually_due.length > 0))
            ) {
                setStripeStage(1);
            }

            if (accountResponse?.payouts_enabled === false &&
                accountResponse?.details_submitted === true &&
                ((accountResponse?.currently_due && accountResponse?.currently_due.length === 0) ||
                    (accountResponse?.eventually_due && accountResponse?.eventually_due.length === 0))
            ) {
                setStripeStage(2);
            }

            if (accountResponse?.payouts_enabled === true &&
                (accountResponse?.currently_due && accountResponse?.currently_due.length) === 0 &&
                (accountResponse?.past_due && accountResponse?.past_due.length) === 0 &&
                (accountResponse?.eventually_due && accountResponse?.eventually_due.length > 0)
            ) {
                setStripeStage(3);
            }

            if (accountResponse?.payouts_enabled === true) {
                setStripeStage(4);
            }
            increaseDoneTasks();

            const onboardingResponse = await stripeOnboard(HTTPMethods.PUT);

            if (onboardingResponse?.onboarding_url !== undefined) {
                setStripeURL(onboardingResponse.onboarding_url);
            }

            const loginResponse = await stripeLogin(HTTPMethods.PUT);

            if (loginResponse?.login_url !== undefined) {
                setStripeURL(loginResponse.login_url);
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [venueId, stripeAccountDataLoaded]);

    useEffect(() => {
        if (!permissions || !permissions[PERMISSIONS.BILLING_AND_PAYMENT]) return;
        if (stripeURL) {
            dispatch(setPaymentURL(stripeURL));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripeURL]);
    useEffect(() => {
        if (!permissions || !permissions[PERMISSIONS.BILLING_AND_PAYMENT]) return;
        if (paymentDetails.paymentProcessor !== PaymentProcessor.PAY360) {
            if (stripeOnboardIsError || stripeLoginIsError || stripeAccountIsError) {
                dispatch(setIsPaymentError(stripeOnboardIsError || stripeLoginIsError || stripeAccountIsError));
            }else{ 
                dispatch(setIsPaymentError(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripeOnboardIsError  , stripeLoginIsError , stripeAccountIsError , permissions]);
    useEffect(() => {
        if (!permissions || !permissions[PERMISSIONS.BILLING_AND_PAYMENT]) return;
        if (stripeStage > -1 && paymentDetails.paymentProcessor !== PaymentProcessor.PAY360) {
            dispatch(setPaymentStage(stripeStage));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripeStage]);
}